import socket, { getChatConnection, subscribeChatConnection } from "@src/api/socket";
import * as localStorage from "@utility/storage/local";
import * as sessionStorage from "@utility/storage/session";
import { tokenPayload } from "@utility/storage/auth";
import { loginIdentity } from "./chatOutboxRules";
import { allowChatFiles, conversationsWithFiles } from "@src/api/lib/chatFileOwners";
import { forgetChatFiles, forgetConversationFiles } from "./chatFiles";
import { forgetPendingForwards } from "./chatForwardPending";
import { forgetDraftMemory } from "./chatDrafts";
import { forgetLastConversations } from "./chatLastConversation";
import {
  createSnapshotGuard,
  hideInSnapshot,
  snapshotsToDrop,
  snapshotTrusted,
  takeThreadSnapshot,
  usableThreadSnapshot,
} from "./chatThreadCache";

/* ----- The chats last left, kept for this browser tab only (see chatThreadCache for the
   rules). Read by a chat as it opens, written only by a chat as it's left - the open chat's
   own state stays the one that's live ----- */

// Who is logged in: the same login the outbox and the API requests use (user, company,
// session) - a refreshed token is the same login
const currentChatLogin = () => {
  const jwt =
    localStorage.getItem(localStorage.AUTH_TOKEN_KEY) || sessionStorage.getItem(sessionStorage.AUTH_TOKEN_KEY);
  return loginIdentity(jwt, jwt ? tokenPayload(jwt) : null);
};

const snapshots = new Map(); // `${owner}|${conversationId}` -> snapshot
const guard = createSnapshotGuard();
const keyOf = (owner, conversationId) => `${owner}|${conversationId}`;

/* ----- What's known of my access, and since when. My chat list as last fetched (which chats,
   and which I only oversee) - so coming back to Chat from elsewhere in Care Diary, before the
   list has loaded again, still knows them. Removals reach this tab only over the connection: a
   list fetched before the tab was away a long time, before the connection was last made, or
   before a "your chats changed" that named no chat may have missed one - and with no connection
   none can arrive. A chat is then asked for first (chatThreadCache snapshotTrusted) ----- */
// Hidden at least this long counts as away (the connection usually stays up behind a hidden
// tab, and removals still reach it - a dropped one is counted on its own)
const AWAY_MS = 60 * 1000;
const clock = { resumedAt: 0, connectedAt: 0, untrustedAt: 0 };
let lastList = null; // { owner, fetchedAt, ids: Set, overseen: Set }
let hiddenAt = 0;

export const rememberChatList = (conversations) => {
  const owner = currentChatLogin();
  lastList = {
    owner,
    fetchedAt: Date.now(),
    ids: new Set(conversations.map((c) => c.id)),
    overseen: new Set(conversations.filter((c) => c.viewingAsAdmin).map((c) => c.id)),
  };
  // A chat kept but no longer in my list (taken out while this tab was hidden or offline, so no
  // removal reached it): it goes now, with its files
  [...snapshots.keys()]
    .filter((k) => k.startsWith(`${owner}|`))
    .map((k) => k.slice(owner.length + 1))
    .filter((conversationId) => !lastList.ids.has(conversationId))
    .forEach((conversationId) => dropThreadSnapshot(conversationId)); // eslint-disable-line no-use-before-define
  // Files kept from a chat no longer in my list (with or without a snapshot) go too; one listed
  // again (let back in) can keep files again
  allowChatFiles([...lastList.ids]);
  const listed = lastList.ids;
  conversationsWithFiles().then((withFiles) =>
    withFiles
      .filter((conversationId) => !listed.has(conversationId))
      .forEach((conversationId) => forgetConversationFiles(conversationId))
  );
};
const listForNow = () => (lastList && lastList.owner === currentChatLogin() ? lastList : null);
export const chatListTrusted = () => {
  const list = listForNow();
  return !!list && snapshotTrusted({ listFetchedAt: list.fetchedAt, ...clock, connected: socket.connected });
};
// Before the list has loaded: whether a chat was in it as last fetched (undefined: not known)
export const lastListed = (conversationId) => {
  const list = listForNow();
  if (!list) return { listed: undefined, overseeing: false };
  return { listed: list.ids.has(conversationId), overseeing: list.overseen.has(conversationId) };
};
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") hiddenAt = Date.now();
    else if (hiddenAt && Date.now() - hiddenAt >= AWAY_MS) clock.resumedAt = Date.now();
  });
}
socket.on("connect", () => {
  clock.connectedAt = Date.now();
});

/* ----- A chat opening: what's kept of it, and whether it can be shown straight away
   ("validate": the chat is asked for first, and it's shown only once the answer says it's
   still mine) - null: load it as usual. And the mark it needs to keep a snapshot when it's left.
   listed: in my chat list as it is now (true / false), or undefined while it's loading ----- */
export const openThreadSnapshot = (conversationId, { listed, overseeing }) => {
  const owner = currentChatLogin();
  const key = keyOf(owner, conversationId);
  // The list not loaded yet: as it was last fetched
  const known = listed === undefined ? lastListed(conversationId) : { listed, overseeing };
  const kept = snapshots.get(key);
  // Whether I'm still allowed in is checked separately (validate) - here only whose it is and
  // that it isn't a group I only oversee. How long ago it was left doesn't matter
  const snapshot = usableThreadSnapshot(kept, {
    owner,
    listed: known.listed !== false,
    overseeing: overseeing || known.overseeing,
  });
  // No longer usable: not kept around either
  if (!snapshot) snapshots.delete(key);
  const validate = !!snapshot && !(known.listed === true && chatListTrusted());
  return { owner, openedAt: guard.generation(conversationId), snapshot, validate };
};

/* ----- A held snapshot about to be shown (validate: the chat has just answered that it's
   mine): only if nothing dropped it while the check ran (cleared elsewhere, taken out, access
   changed) and it's still the same login - otherwise the chat loads as on a first open ----- */
export const snapshotStillValid = (conversationId, opened) =>
  !!opened && guard.canKeep(conversationId, opened.openedAt) && currentChatLogin() === opened.owner;

// Where each chat was left, by the same key as its snapshot (leftPlace below)
const leftPlaces = new Map();

/* ----- A chat being left. Kept only if nothing was dropped for it since it opened, and it's
   still the same login (logging out mid-chat keeps nothing) ----- */
export const keepThreadSnapshot = (conversationId, opened, state) => {
  if (!opened || !guard.canKeep(conversationId, opened.openedAt)) return;
  const owner = currentChatLogin();
  if (!owner || owner !== opened.owner) return;
  const snapshot = takeThreadSnapshot({ ...state, owner, conversationId }, Date.now());
  const key = keyOf(owner, conversationId);
  if (!snapshot) {
    snapshots.delete(key);
    return;
  }
  // Moved to the end: the newest left. Then the least recently used go, beyond the number of
  // chats or the size budget - nothing goes by age
  snapshots.delete(key);
  snapshots.set(key, snapshot);
  const kept = [...snapshots.entries()].map(([k, s]) => ({
    key: k,
    owner: s.owner,
    savedAt: s.savedAt,
    bytes: s.bytes ?? 0,
  }));
  // A chat's place goes with its snapshot (the same key), so neither outlives the other
  snapshotsToDrop(kept, { owner }).forEach((k) => {
    snapshots.delete(k);
    leftPlaces.delete(k);
  });
};

/* ----- Where each chat was left (a message number and a distance), for coming back to it
   there - per login, and gone with the snapshots (declared above keepThreadSnapshot) ----- */
export const leftPlace = (conversationId) => leftPlaces.get(keyOf(currentChatLogin(), conversationId));
export const rememberLeftPlace = (conversationId, place) =>
  leftPlaces.set(keyOf(currentChatLogin(), conversationId), place);

/* ----- Taken out of a chat (removed, left, deleted elsewhere, no longer allowed in) ----- */
export const dropThreadSnapshot = (conversationId, onScreen = []) => {
  guard.dropChat(conversationId);
  const messageIds = new Set(onScreen.filter((m) => m.attachment).map((m) => m.id));
  [...snapshots.keys()]
    .filter((k) => k.endsWith(`|${conversationId}`))
    .forEach((k) => {
      snapshots.get(k).messages.forEach((m) => m.attachment && messageIds.add(m.id));
      snapshots.delete(k);
    });
  [...leftPlaces.keys()].filter((k) => k.endsWith(`|${conversationId}`)).forEach((k) => leftPlaces.delete(k));
  // Its files go with it - those of these messages and every other kept from this chat
  // (chatFileOwners) - their addresses in this tab, and their copies in this browser
  forgetConversationFiles(conversationId, [...messageIds]);
};

/* ----- The server wouldn't let me into a chat's live updates (not allowed in now): what's kept
   of it isn't trusted any more, and a chat still open then keeps nothing when it's left. Its
   files stay until the server says I have lost the chat itself (dropThreadSnapshot below) ----- */
export const distrustThreadSnapshot = (conversationId) => {
  guard.dropChat(conversationId);
  [...snapshots.keys()].filter((k) => k.endsWith(`|${conversationId}`)).forEach((k) => snapshots.delete(k));
};

/* ----- My access changed, or chat stopped for this login (refused, session ended) ----- */
const dropAllThreadSnapshots = () => {
  guard.dropAll();
  snapshots.clear();
};

/* ----- Logged out: nothing of this person's chats is left in this tab - snapshots, places,
   downloaded files' addresses, forwards on their way, drafts in memory, the chat last open ----- */
export const forgetChatSession = () => {
  dropAllThreadSnapshots();
  lastList = null;
  leftPlaces.clear();
  forgetChatFiles();
  forgetPendingForwards();
  forgetDraftMemory();
  forgetLastConversations();
};

// Heard whatever screen is open - a chat taken from me while I'm elsewhere in Care Diary goes too
socket.on("chat:conversation_removed", ({ conversationId }) => dropThreadSnapshot(conversationId));
socket.on("chat:conversations_changed", ({ conversationId, removed }) => {
  // No chat named: which changed isn't known - nothing kept is trusted until the list (which
  // refetches on this) comes back, and drops any chat no longer in it (rememberChatList)
  if (!conversationId) {
    clock.untrustedAt = Date.now();
    if (removed) dropAllThreadSnapshots();
    return;
  }
  if (removed) dropThreadSnapshot(conversationId);
});
// Cleared on another device: opened afresh rather than from what was there before
socket.on("chat:conversation_cleared", ({ conversationId, removed }) => {
  if (removed) dropThreadSnapshot(conversationId);
  else {
    guard.dropChat(conversationId);
    [...snapshots.keys()].filter((k) => k.endsWith(`|${conversationId}`)).forEach((k) => snapshots.delete(k));
  }
});
// Deleted for me on another device or tab: out of what's kept of that chat (an open chat takes
// it out of its own messages - and so out of the snapshot it keeps when it's left)
socket.on("chat:message_hidden", ({ conversationId, messageId } = {}) => {
  const key = keyOf(currentChatLogin(), conversationId);
  const kept = snapshots.get(key);
  if (kept && messageId) snapshots.set(key, hideInSnapshot(kept, messageId));
});
socket.on("chat:access_changed", dropAllThreadSnapshots);
subscribeChatConnection(() => {
  const { problem } = getChatConnection();
  if (problem === "denied" || problem === "session-ended") dropAllThreadSnapshots();
});
